import { Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import SEOSchema from '@/components/feature/SEOSchema';
import { mangaArticles, categories, siteInfo, faqData } from '@/mocks/mangaData';

export default function Home() {
  const navigate = useNavigate();

  const featuredArticles = mangaArticles.filter(a => a.rating >= 9.0).slice(0, 4);
  const latestArticles = [...mangaArticles].sort(
    (a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime()
  ).slice(0, 8);
  const topRatedArticles = [...mangaArticles].sort((a, b) => b.rating - a.rating).slice(0, 4);

  const handleCategoryClick = (slug: string) => {
    navigate(`/category/${slug}`);
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  };

  return (
    <>
      <SEOSchema type="Organization" />
      <SEOSchema type="WebSite" />
      <SEOSchema
        type="WebPage"
        pageTitle="bl漫画精选推荐 - 最新最全bl漫画在线阅读"
        pageDescription={siteInfo.description}
        pageUrl="/"
      />
      <SEOSchema type="FAQPage" faqData={faqData} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full h-[500px] md:h-[620px] overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Beautiful%20abstract%20artistic%20manga%20illustration%20with%20soft%20warm%20pink%20and%20peach%20gradients%2C%20delicate%20cherry%20blossom%20petals%20floating%2C%20dreamy%20bokeh%20lights%2C%20stylized%20Japanese-inspired%20pattern%2C%20elegant%20flowing%20lines%2C%20gentle%20romantic%20atmosphere%2C%20no%20characters%2C%20abstract%20decorative&width=1800&height=720&seq=hero-bg-bl-manga&orientation=landscape"
              alt="BL漫画精选 - 最新最全bl漫画在线阅读平台"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/15 to-black/35"></div>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 w-full">
              <h1 className="font-heading text-3xl md:text-5xl text-background-50 mb-4 leading-tight">
                bl漫画精选推荐
              </h1>
              <p className="text-base md:text-lg text-background-200 max-w-2xl leading-relaxed mb-8">
                最新最全bl漫画在线阅读平台，精选高分bl漫画作品推荐<br />
                涵盖甜宠、虐心、古风、现代等多种题材，每日更新
              </p>
              <div className="flex flex-col sm:flex-row items-center gap-3">
                <button
                  onClick={() => handleCategoryClick('tian-chong')}
                  className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full font-medium text-sm transition-all cursor-pointer whitespace-nowrap"
                >
                  开始阅读
                </button>
                <Link
                  to="/about"
                  className="px-8 py-3 bg-background-50/20 hover:bg-background-50/30 backdrop-blur-sm text-background-50 rounded-full font-medium text-sm transition-all whitespace-nowrap"
                >
                  了解更多
                </Link>
              </div>
            </div>
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-8">
              <div className="text-center">
                <div className="text-3xl font-heading text-accent-400">{siteInfo.stats.totalWorks.toLocaleString()}+</div>
                <div className="text-xs text-background-300 mt-1">精选作品</div>
              </div>
              <div className="w-px h-8 bg-background-400/30"></div>
              <div className="text-center">
                <div className="text-3xl font-heading text-accent-400">{siteInfo.stats.monthlyActive.toLocaleString()}+</div>
                <div className="text-xs text-background-300 mt-1">月活读者</div>
              </div>
              <div className="w-px h-8 bg-background-400/30"></div>
              <div className="text-center">
                <div className="text-3xl font-heading text-accent-400">{siteInfo.stats.avgRating}</div>
                <div className="text-xs text-background-300 mt-1">平均评分</div>
              </div>
            </div>
          </section>

          {/* Category Navigation */}
          <section className="w-full px-4 md:px-6 py-10 md:py-14" aria-label="分类导航">
            <div className="max-w-7xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 text-center mb-8">
                探索分类
              </h2>
              <div className="flex flex-wrap justify-center gap-3">
                {categories.map((cat) => (
                  <button
                    key={cat.slug}
                    onClick={() => handleCategoryClick(cat.slug)}
                    className="px-6 py-2.5 rounded-full text-sm font-medium bg-background-100 hover:bg-primary-100 hover:text-primary-700 text-foreground-600 transition-all cursor-pointer whitespace-nowrap border border-background-200/70 hover:border-primary-200"
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* Featured Works */}
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100" aria-label="精选推荐">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl text-foreground-900">
                    精选推荐
                  </h2>
                  <p className="text-sm text-foreground-500 mt-1">高分bl漫画作品，品质保证</p>
                </div>
                <Link
                  to="/category/all"
                  className="text-sm text-primary-500 hover:text-primary-600 font-medium flex items-center gap-1 whitespace-nowrap"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'instant' as const })}
                >
                  查看全部
                  <i className="ri-arrow-right-line text-xs"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {featuredArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            </div>
          </section>

          {/* Latest Updates */}
          <section className="w-full px-4 md:px-6 py-10 md:py-14" aria-label="最新更新">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl text-foreground-900">
                    最新更新
                  </h2>
                  <p className="text-sm text-foreground-500 mt-1">每日更新最新bl漫画内容</p>
                </div>
                <Link
                  to="/category/all"
                  className="text-sm text-primary-500 hover:text-primary-600 font-medium flex items-center gap-1 whitespace-nowrap"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'instant' as const })}
                >
                  查看全部
                  <i className="ri-arrow-right-line text-xs"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {latestArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            </div>
          </section>

          {/* Top Rated */}
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100" aria-label="高分排行">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl text-foreground-900">
                    高分排行
                  </h2>
                  <p className="text-sm text-foreground-500 mt-1">读者评分最高的bl漫画作品</p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {topRatedArticles.map((article, index) => (
                  <div key={article.id} className="relative">
                    <div className="absolute -top-2 -left-2 z-10 w-8 h-8 rounded-full bg-accent-500 flex items-center justify-center text-background-50 font-bold text-sm">
                      {index + 1}
                    </div>
                    <ArticleCard article={article} />
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="w-full px-4 md:px-6 py-10 md:py-14" aria-label="常见问题">
            <div className="max-w-3xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 text-center mb-8">
                常见问题
              </h2>
              <div className="space-y-3">
                {faqData.map((faq, index) => (
                  <details
                    key={index}
                    className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden"
                  >
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer font-medium text-foreground-800 text-sm hover:text-primary-500 transition-colors">
                      {faq.question}
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform text-lg"></i>
                    </summary>
                    <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                      {faq.answer}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100 text-center" aria-label="行动号召">
            <div className="max-w-2xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-4">
                开启你的bl漫画之旅
              </h2>
              <p className="text-foreground-500 mb-8 leading-relaxed">
                探索海量bl漫画资源，发现属于你的那一部心仪之作
              </p>
              <button
                onClick={() => handleCategoryClick('all')}
                className="px-10 py-3.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full font-medium text-sm transition-all cursor-pointer whitespace-nowrap"
              >
                立即浏览全部作品
              </button>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}